import { Link, usePage } from "@inertiajs/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styled from "styled-components";

import {
  LandingLanguageProvider,
  useLandingLanguage,
} from "../../../contexts/landing-language";
import { categoryPath, insightsHome } from "../../../components/insights/paths";
import ArticleCard from "../../../components/insights/ArticleCard";
import Breadcrumb from "../../../components/insights/Breadcrumb";
import Footer from "../home/Footer";
import Header from "../home/Header";
import SeoHead from "../../../components/insights/SeoHead";

const Page = styled.div`
  background: #faf9f7;
  min-height: 100vh;
`;

const Hero = styled.section`
  position: relative;
  color: #fff;
  padding: 168px 40px 64px;
  background-image:
    linear-gradient(
      180deg,
      rgba(26, 26, 26, 0.6) 0%,
      rgba(26, 26, 26, 0.82) 100%
    ),
    url(${(props) => props.$src});
  background-size: cover;
  background-position: center;

  @media (max-width: 768px) {
    padding: 140px 20px 56px;
  }
`;

// Match the marketing Header's inner box so hero + content line up with the logo.
const HeroInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const CrumbWrap = styled.div`
  margin-bottom: 22px;

  a,
  span {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(32px, 5vw, 50px);
  font-weight: 800;
  margin: 0 0 14px;
`;

const HeroDesc = styled.p`
  font-size: clamp(15px, 2vw, 18px);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
  margin: 0;
  max-width: 680px;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Section = styled.section`
  padding: 56px 0;
`;

const SectionTitle = styled.h2`
  font-size: clamp(20px, 3vw, 26px);
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 22px;
`;

const Empty = styled.p`
  color: #5e6278;
  font-size: 15px;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 0 96px;
`;

const PagerLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 999px;
  border: 1px solid #e3ded6;
  background: #fff;
  color: #1a1a1a;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: #c9a961;
    transform: translateY(-2px);
  }
`;

const PagerInfo = styled.span`
  font-size: 13px;
  color: #5e6278;
`;

const InsightCategory = () => {
  const { t, language } = useLandingLanguage();
  const { category, featured, articles, pagination } = usePage().props;
  const copy = t.insights;

  const base = categoryPath(language, category.slug);
  const page = pagination?.page || 1;
  const pageCount = pagination?.page_count || 1;

  const crumbs = [
    {
      label: copy.breadcrumb.home,
      href: language === "id" ? "/id/hebe" : "/hebe",
    },
    { label: copy.breadcrumb.insights, href: insightsHome(language) },
    { label: category.name },
  ];

  const seo = category.seo || {};

  return (
    <Page>
      <SeoHead
        title={seo.meta_title || `${category.name} — HEBE Insights`}
        description={seo.meta_description || category.description}
      />
      <Header forceScrolled />

      <Hero $src={category.cover_url}>
        <HeroInner>
          <CrumbWrap>
            <Breadcrumb items={crumbs} />
          </CrumbWrap>
          <HeroTitle>{category.name}</HeroTitle>
          {category.description ? (
            <HeroDesc>{category.description}</HeroDesc>
          ) : null}
        </HeroInner>
      </Hero>

      <Container>
        {featured?.length ? (
          <Section>
            <SectionTitle>{copy.index.featured}</SectionTitle>
            <Grid>
              {featured.map((article) => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  showCategory={false}
                />
              ))}
            </Grid>
          </Section>
        ) : null}

        <Section>
          <SectionTitle>{copy.category.allArticles}</SectionTitle>
          {articles?.length ? (
            <Grid>
              {articles.map((article) => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  showCategory={false}
                />
              ))}
            </Grid>
          ) : (
            <Empty>{copy.category.empty}</Empty>
          )}
        </Section>
      </Container>

      {pageCount > 1 ? (
        <Pager>
          {page > 1 ? (
            <PagerLink href={`${base}?page=${page - 1}`}>
              <ChevronLeft size={15} /> {copy.pagination.prev}
            </PagerLink>
          ) : null}
          <PagerInfo>
            {copy.pagination.page} {page} {copy.pagination.of} {pageCount}
          </PagerInfo>
          {page < pageCount ? (
            <PagerLink href={`${base}?page=${page + 1}`}>
              {copy.pagination.next} <ChevronRight size={15} />
            </PagerLink>
          ) : null}
        </Pager>
      ) : null}

      <Footer />
    </Page>
  );
};

const InsightCategoryPage = () => {
  return (
    <LandingLanguageProvider>
      <InsightCategory />
    </LandingLanguageProvider>
  );
};

export default InsightCategoryPage;
